import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Ghost, Sparkles, RefreshCw, ArrowUpRight, XCircle,
  CheckCircle2, History, Zap, ChevronDown, ChevronUp,
} from "lucide-react";
import { fmtINR, stageMeta } from "./bizdevUtils";

/** Iter 171 · BizDev Phase 3 — AI Resurrection Candidates.
 *  Scans killed/parked ideas with Claude + their research notes and surfaces
 *  ideas whose original kill reason may no longer hold. */

function ConfidenceBar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const tone = v >= 75 ? "bg-emerald-500" : v >= 55 ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2 min-w-[140px]" data-testid="resurrection-confidence">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">{v}%</span>
    </div>
  );
}

function CandidateCard({ c, onAct }) {
  const [busy, setBusy] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const act = async (action) => {
    setBusy(action);
    try {
      await api.post(`/workspaces/${c.workspace_id}/bizdev/resurrections/${c.id}/act`, {
        action,
        target_stage: c.suggested_stage || "spark",
      });
      toast.success(action === "resurrect"
        ? `Resurrected "${c.idea_title}" to ${c.suggested_stage || "spark"}`
        : `Dismissed "${c.idea_title}"`);
      onAct();
    } catch (e) {
      toast.error(apiErrorMessage(e, `Failed to ${action}`));
    } finally {
      setBusy(null);
    }
  };

  const stage = stageMeta(c.orig_stage);
  const months = c.months_since_last_touch;

  return (
    <div className="border border-border bg-card p-4 flex flex-col gap-3"
         data-testid={`resurrection-card-${c.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${stage.color}`}>
              from {stage.label}
            </span>
            {c.should_resurrect ? (
              <span className="text-[9px] uppercase tracking-widest border border-violet-300 text-violet-700 bg-violet-50 px-1.5 py-0.5 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Resurrect
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-widest border border-slate-300 text-slate-500 bg-slate-50 px-1.5 py-0.5">
                Stay dead
              </span>
            )}
            {months !== null && months !== undefined && (
              <span className="text-[10px] text-muted-foreground">
                shelved ~{months}mo ago
              </span>
            )}
            {c.notes_count > 0 && (
              <span className="text-[10px] text-muted-foreground">
                · {c.notes_count} research note{c.notes_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="mt-1 font-semibold text-sm truncate">{c.idea_title}</div>
        </div>
        <ConfidenceBar value={c.confidence} />
      </div>

      <div className="text-xs text-foreground/90 leading-relaxed" data-testid="resurrection-rationale">
        {c.rationale || <span className="text-muted-foreground italic">No rationale returned.</span>}
      </div>

      {c.pivot_angle && (
        <div className="text-xs bg-violet-50 border border-violet-200 px-3 py-2 rounded-sm">
          <span className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">Pivot angle</span>
          <div className="text-violet-900 mt-0.5">{c.pivot_angle}</div>
        </div>
      )}

      {c.suggested_next_experiment && (
        <div className="text-xs bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm flex items-start gap-2">
          <Zap className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Next experiment</div>
            <div className="text-amber-900 mt-0.5">{c.suggested_next_experiment}</div>
          </div>
        </div>
      )}

      <button type="button" className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 self-start"
              onClick={() => setShowAll((s) => !s)}>
        {showAll ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {showAll ? "Hide" : "Show"} details
      </button>
      {showAll && (
        <div className="text-[11px] text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-1">
          <div>Suggested stage: <b className="text-foreground">{c.suggested_stage}</b></div>
          <div>Revenue ballpark: <b className="text-foreground">{fmtINR(c.revenue_likely)}</b></div>
          {c.category && <div>Category: <b className="text-foreground">{c.category}</b></div>}
          {c.tags?.length > 0 && (
            <div className="col-span-2">Tags: {c.tags.map((t) => (
              <span key={t} className="inline-block mr-1 px-1.5 py-0.5 bg-muted rounded-sm text-[10px]">{t}</span>
            ))}</div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button type="button" disabled={!!busy}
                onClick={() => act("resurrect")}
                className="inline-flex items-center gap-1.5 text-xs bg-primary text-primary-foreground px-3 py-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity"
                data-testid={`resurrection-act-resurrect-${c.id}`}>
          <ArrowUpRight className="w-3.5 h-3.5" />
          {busy === "resurrect" ? "Resurrecting…" : `Resurrect → ${c.suggested_stage}`}
        </button>
        <button type="button" disabled={!!busy}
                onClick={() => act("dismiss")}
                className="inline-flex items-center gap-1.5 text-xs border border-border px-3 py-1.5 hover:bg-muted transition-colors disabled:opacity-50"
                data-testid={`resurrection-act-dismiss-${c.id}`}>
          <XCircle className="w-3.5 h-3.5" />
          {busy === "dismiss" ? "Dismissing…" : "Dismiss"}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ hasScans, onScan, scanning }) {
  return (
    <div className="border border-dashed border-border p-12 text-center" data-testid="resurrection-empty">
      <Ghost className="w-10 h-10 mx-auto text-muted-foreground" />
      <div className="mt-3 text-sm font-medium">
        {hasScans ? "No pending resurrection candidates" : "Nothing scanned yet"}
      </div>
      <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
        {hasScans
          ? "Every candidate has been resurrected or dismissed. Fresh research clips → next Sunday scan."
          : "Run an AI scan across your killed & parked ideas. Claude re-reads each idea + its research notes and flags any whose original kill reason may no longer hold."}
      </div>
      <button type="button" disabled={scanning}
              onClick={onScan}
              className="mt-4 inline-flex items-center gap-1.5 text-xs bg-primary text-primary-foreground px-4 py-2 hover:opacity-90 disabled:opacity-50"
              data-testid="resurrection-scan-empty-btn">
        <Sparkles className="w-3.5 h-3.5" />
        {scanning ? "Scanning…" : "Run scan now"}
      </button>
    </div>
  );
}

export default function ResurrectionsTab({ wsId }) {
  const [data, setData] = useState({ rows: [], count: 0, last_scan: null });
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/bizdev/resurrections`, { params: { status: tab } });
      // API returns rows with workspace_id but we also want it on the candidate for the act() call.
      const rows = (r.data.rows || []).map((c) => ({ ...c, workspace_id: c.workspace_id || wsId }));
      setData({ ...r.data, rows });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load candidates"));
    } finally {
      setLoading(false);
    }
  }, [wsId, tab]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showHistory) return;
    api.get(`/workspaces/${wsId}/bizdev/resurrections/history`)
      .then((r) => setHistory(r.data.rows || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load history")));
  }, [wsId, showHistory]);

  const scan = async () => {
    setScanning(true);
    const t = toast.loading("Scanning killed & parked ideas…");
    try {
      const r = await api.post(`/workspaces/${wsId}/bizdev/resurrections/scan`, {
        limit: 20, include_stages: ["killed", "parked"], force: false,
      });
      toast.success(
        `Scanned ${r.data.ideas_scanned} ideas · ${r.data.resurrect_count} flagged to resurrect`,
        { id: t });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Scan failed"), { id: t });
    } finally {
      setScanning(false);
    }
  };

  const lastScan = data.last_scan;
  const lastScanAt = lastScan?.finished_at ? new Date(lastScan.finished_at) : null;

  const grouped = useMemo(() => {
    const resurrect = data.rows.filter((r) => r.should_resurrect);
    const staydead = data.rows.filter((r) => !r.should_resurrect);
    return { resurrect, staydead };
  }, [data.rows]);

  return (
    <div className="space-y-4" data-testid="bizdev-resurrections">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-xs text-muted-foreground max-w-md">
          Claude re-reads every killed & parked idea + its research notes and surfaces
          the ones worth a second look. Auto-runs every Sunday night.
        </div>
        <div className="ml-auto flex items-center gap-2">
          {lastScanAt && (
            <span className="text-[10px] text-muted-foreground" data-testid="resurrection-last-scan">
              Last scan: {lastScanAt.toLocaleDateString()} · {lastScan.candidates_count} analyzed · {lastScan.resurrect_count} flagged
            </span>
          )}
          <button type="button" onClick={() => setShowHistory((s) => !s)}
                  className="inline-flex items-center gap-1.5 text-xs border border-border px-3 py-1.5 hover:border-primary hover:text-primary transition-colors"
                  data-testid="resurrection-history-btn">
            <History className="w-3.5 h-3.5" /> History
          </button>
          <button type="button" onClick={scan} disabled={scanning}
                  className="inline-flex items-center gap-1.5 text-xs bg-primary text-primary-foreground px-3 py-1.5 hover:opacity-90 disabled:opacity-50"
                  data-testid="resurrection-scan-btn">
            {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {scanning ? "Scanning…" : "Scan now"}
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="border border-border bg-card p-4" data-testid="resurrection-history">
          <div className="text-xs font-semibold mb-2">Scan history</div>
          {history.length === 0 ? (
            <div className="text-xs text-muted-foreground">No scans yet.</div>
          ) : (
            <div className="divide-y">
              {history.map((h) => (
                <div key={h.id} className="py-2 text-xs flex items-center gap-3">
                  <span className="font-mono text-[10px] text-muted-foreground w-32">
                    {new Date(h.created_at).toLocaleString()}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {h.trigger}
                  </span>
                  <span className="text-muted-foreground">
                    {h.ideas_scanned} ideas → {h.candidates_count} analyzed · <b className="text-violet-700">{h.resurrect_count} to resurrect</b>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center border-b border-border">
        {[
          { key: "pending", label: "Pending" },
          { key: "resurrected", label: "Resurrected" },
          { key: "dismissed", label: "Dismissed" },
          { key: "all", label: "All" },
        ].map(({ key, label }) => (
          <button key={key} type="button" onClick={() => setTab(key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors border-b-2 ${tab === key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  data-testid={`resurrection-status-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading candidates…</div>
      ) : data.rows.length === 0 ? (
        <EmptyState hasScans={!!lastScan} onScan={scan} scanning={scanning} />
      ) : tab === "pending" ? (
        <>
          {grouped.resurrect.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold mt-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> AI recommends resurrecting ({grouped.resurrect.length})
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {grouped.resurrect.map((c) => (
                  <CandidateCard key={c.id} c={c} onAct={load} />
                ))}
              </div>
            </>
          )}
          {grouped.staydead.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-4 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Analyzed — stay shelved ({grouped.staydead.length})
              </div>
              <div className="grid md:grid-cols-2 gap-3 opacity-80">
                {grouped.staydead.map((c) => (
                  <CandidateCard key={c.id} c={c} onAct={load} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {data.rows.map((c) => (
            <CandidateCard key={c.id} c={c} onAct={load} />
          ))}
        </div>
      )}
    </div>
  );
}
